import { apiClient } from '@/shared/lib/axios';

export type ClinicOption = { id: number; clinic_number: string; service_type?: string | null };

export type ClinicLocationListItem = {
  id: number;
  clinic_id: number;
  clinic_number?: string;
  serial_number: number;
  location_name: string;
  service_type?: string | null;
  address1: string;
  address2: string;
  available_from: string;
  available_to: string | null;
  site_type?: string | null;
};

export type ClinicLocationsListParams = {
  page?: number;
  per_page?: number;
  location_name?: string;
  available_date?: string;
  /** For super-admin: filter by サイト区分 */
  site_types?: string[];
};

export type ClinicLocationsListResponse = {
  success: boolean;
  data: ClinicLocationListItem[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export const getClinicLocationsList = async (
  siteType: string,
  params: ClinicLocationsListParams
): Promise<ClinicLocationsListResponse> => {
  const response = await apiClient.get<ClinicLocationsListResponse>(
    `/admin/${siteType}/clinic-locations`,
    { params }
  );
  return response.data;
};
export type HealthCenterRegistTypeOption = { id: number; name: string };

export type ClinicLocationStorePayload = {
  clinic_id: number;
  service_type: 'A2' | 'R2' | 'BOTH';
  location_name: string;
  location_name_kana?: string;
  available_from: string;
  available_to?: string;
  health_center_regist_type_id: number;
  postal_code: string;
  address1: string;
  address2: string;
  address3?: string;
  telephone: string;
  fax?: string;
  rezept_address?: string;
  base_name?: string;
  is_compnay?: 0 | 1 | null;
  founder_name?: string;
  founder_name_kana?: string;
  founder_postal_code?: string;
  founder_address1?: string;
  founder_address2?: string;
  founder_address3?: string;
  founder_date_of_birth?: string;
  founder_telephone?: string;
};

export const createClinicLocation = async (
  siteType: string,
  payload: ClinicLocationStorePayload
) => {
  const response = await apiClient.post<{
    success: boolean;
    data: { id: number };
    message?: string;
  }>(`/admin/${siteType}/clinic-locations`, payload);
  return response.data;
};

export type ClinicLocationMassagerItem = {
  id: number;
  massager_id?: number;
  serial_number?: string;
  name: string;
  /** はり・きゅう (A2/BOTH 行の集約) */
  available_from: string | null;
  available_to: string | null;
  /** マッサージ (R2/BOTH 行の集約) */
  available_from_r2?: string | null;
  available_to_r2?: string | null;
  /** 資格 display (e.g. はり師、きゅう師、マッサージ師) from massager license fields */
  certificate?: string | null;
  /** Massager service_type: A2, R2, BOTH */
  service_type?: string | null;
  /** Has はり・きゅう cert (for A2 manager) */
  has_a2_cert?: boolean;
  /** Has マッサージ cert (for R2 manager) */
  has_r2_cert?: boolean;
};

export type ClinicLocationManagerItem = {
  id: number;
  massager_id?: number;
  name: string;
  service_type?: string | null;
  available_from: string;
  available_to: string | null;
  /** HIT-1853: Service-specific periods for admin table */
  available_from_a2?: string | null;
  available_to_a2?: string | null;
  available_from_r2?: string | null;
  available_to_r2?: string | null;
  /** Per-service manager ids for edit links (when therapist has A2+R2) */
  manager_entries?: Array<{
    id: number;
    service_type: string | null;
    available_to?: string | null;
  }>;
};

/** Single clinic location (for edit form and detail). Includes massagers and managers for detail view. */
export type ClinicLocationShowResponse = {
  success: boolean;
  data: ClinicLocationStorePayload & {
    id: number;
    clinic_id: number;
    clinic_number?: string;
    serial_number?: number;
    /** Spec: {clinic_number}_{serial_number} for breadcrumb/title */
    display_id?: string;
    /** From clinic.site_type (e.g. medix, upsystem, hitowa). Shown as サイト区分 for super-admin. */
    site_type?: string | null;
    /** From clinic.service_type: 'r2' | 'a2' | 'both' | null */
    service?: string | null;
    /** True when parent clinic row is soft-deleted (拠点 may still load for read-only). */
    is_parent_clinic_deleted?: boolean;
    available_from: string;
    available_to: string | null;
    founder_date_of_birth?: string | null;
    founder_postal_code?: string | null;
    massagers?: ClinicLocationMassagerItem[];
    managers?: ClinicLocationManagerItem[];
    created_at?: string | null;
    updated_at?: string | null;
    created_by?: string | null;
    updated_by?: string | null;
  };
};

export const getClinicLocation = async (
  siteType: string,
  id: number,
  options?: { showOutsidePeriod?: boolean }
): Promise<ClinicLocationShowResponse> => {
  const response = await apiClient.get<ClinicLocationShowResponse>(
    `/admin/${siteType}/clinic-locations/${id}`,
    {
      params: {
        show_outside_period: options?.showOutsidePeriod ? 1 : 0,
      },
    }
  );
  return response.data;
};

export type ClinicLocationUpdatePayload = Omit<ClinicLocationStorePayload, 'clinic_id'>;

export const updateClinicLocation = async (
  siteType: string,
  id: number,
  payload: ClinicLocationUpdatePayload
) => {
  const response = await apiClient.put<{
    success: boolean;
    data: { id: number };
    message?: string;
  }>(`/admin/${siteType}/clinic-locations/${id}`, payload);
  return response.data;
};

/** Wide area union row for 保険者別番号 prefill (from massager). */
export type ManagerWideAreaRow = {
  wide_area_union_code: string | null;
  wide_area_union_number: string;
  prefecture_number: string;
  /** A2 | R2 | BOTH — filter by block (A2 shows A2+BOTH, R2 shows R2+BOTH). */
  service_type?: string | null;
};

export const getManagerWideArea = async (
  siteType: string,
  clinicLocationId: number,
  massagerId: number
): Promise<{ success: boolean; data: ManagerWideAreaRow[] }> => {
  const response = await apiClient.get<{ success: boolean; data: ManagerWideAreaRow[] }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-managers/wide-area`,
    { params: { massager_id: massagerId } }
  );
  return response.data;
};

/** Single service box (A2 or R2) for store payload. */
export type ClinicLocationManagerServiceBlock = {
  available_from: string;
  available_to?: string | null;
  location_manager_number?: string | null;
  is_none_disp?: boolean;
  wide_area_union_rows?: Array<{
    wide_area_union_code?: string | null;
    wide_area_union_number?: string | null;
    prefecture_number?: string | null;
  }>;
  am_time_start?: string | null;
  am_time_end?: string | null;
  pm_time_start?: string | null;
  pm_time_end?: string | null;
  is_am_monday?: boolean;
  is_am_tuesday?: boolean;
  is_am_wednesday?: boolean;
  is_am_thursday?: boolean;
  is_am_friday?: boolean;
  is_am_saturday?: boolean;
  is_am_sunday?: boolean;
  is_pm_monday?: boolean;
  is_pm_tuesday?: boolean;
  is_pm_wednesday?: boolean;
  is_pm_thursday?: boolean;
  is_pm_friday?: boolean;
  is_pm_saturday?: boolean;
  is_pm_sunday?: boolean;
};

export type ClinicLocationManagerStorePayload = {
  clinic_id: number;
  massager_id: number;
  BLOCK_A2?: ClinicLocationManagerServiceBlock;
  BLOCK_R2?: ClinicLocationManagerServiceBlock;
};

export const createClinicLocationManager = async (
  siteType: string,
  clinicLocationId: number,
  payload: ClinicLocationManagerStorePayload
): Promise<{
  success: boolean;
  data: { id: number; clinic_location_id: number };
  message?: string;
}> => {
  const response = await apiClient.post(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-managers`,
    payload
  );
  return response.data;
};

/** Manager detail for edit form (施術管理者編集). */
export type ClinicLocationManagerDetail = {
  id: number;
  clinic_id: number;
  clinic_location_id: number;
  massager_id: number;
  /** Which information row is edited (A2 | R2); drives form a2 vs r2 mapping. */
  service_type?: string | null;
  /** Per-service blocks when API returns nested shape (same keys as create). */
  BLOCK_A2?: ClinicLocationManagerServiceBlock | null;
  BLOCK_R2?: ClinicLocationManagerServiceBlock | null;
  available_from: string;
  available_to: string | null;
  location_manager_number: string | null;
  is_none_disp: boolean;
  wide_area_union_rows: Array<{
    wide_area_union_code?: string | null;
    wide_area_union_number?: string | null;
    prefecture_number?: string | null;
  }>;
  am_time_start: string | null;
  am_time_end: string | null;
  pm_time_start: string | null;
  pm_time_end: string | null;
  is_am_monday: boolean;
  is_am_tuesday: boolean;
  is_am_wednesday: boolean;
  is_am_thursday: boolean;
  is_am_friday: boolean;
  is_am_saturday: boolean;
  is_am_sunday: boolean;
  is_pm_monday: boolean;
  is_pm_tuesday: boolean;
  is_pm_wednesday: boolean;
  is_pm_thursday: boolean;
  is_pm_friday: boolean;
  is_pm_saturday: boolean;
  is_pm_sunday: boolean;
  location_name: string;
  location_name_kana: string | null;
  serial_number: number | null;
  massager_name: string;
  massager_serial_number: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
};

export const getClinicLocationManager = async (
  siteType: string,
  clinicLocationId: number,
  managerId: number
): Promise<{ success: boolean; data: ClinicLocationManagerDetail }> => {
  const response = await apiClient.get<{ success: boolean; data: ClinicLocationManagerDetail }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-managers/${managerId}`
  );
  return response.data;
};

/** Massager assignment detail for schedule edit form (拠点はり・きゅう師 勤務時間編集). */
export type ClinicLocationMassagerDetail = {
  id: number;
  clinic_id: number;
  clinic_location_id: number;
  massager_id: number;
  am_time_start: string | null;
  am_time_end: string | null;
  pm_time_start: string | null;
  pm_time_end: string | null;
  is_am_monday: boolean;
  is_am_tuesday: boolean;
  is_am_wednesday: boolean;
  is_am_thursday: boolean;
  is_am_friday: boolean;
  is_am_saturday: boolean;
  is_am_sunday: boolean;
  is_pm_monday: boolean;
  is_pm_tuesday: boolean;
  is_pm_wednesday: boolean;
  is_pm_thursday: boolean;
  is_pm_friday: boolean;
  is_pm_saturday: boolean;
  is_pm_sunday: boolean;
  remarks: string | null;
  available_from: string | null;
  available_to: string | null;
  location_name: string;
  location_name_kana: string | null;
  serial_number: number | null;
  massager_name: string;
  massager_serial_number: number | null;
  massager_service_type?: string | null;
  last_visit_date: string | null;
};

/** GET massager edit: one call returns A2 and/or R2 blocks per 拠点 service_type. */
export type ClinicLocationMassagerShowData = {
  A2?: ClinicLocationMassagerDetail;
  R2?: ClinicLocationMassagerDetail;
};

export const getClinicLocationMassager = async (
  siteType: string,
  clinicLocationId: number,
  /** clinic_location_massagers.id (anchor row) */
  clinicLocationMassagerId: number
): Promise<{ success: boolean; data: ClinicLocationMassagerShowData }> => {
  const response = await apiClient.get<{ success: boolean; data: ClinicLocationMassagerShowData }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-massagers/${clinicLocationMassagerId}`
  );
  return response.data;
};

export type ClinicLocationMassagerUpdatePayload = {
  am_time_start?: string | null;
  am_time_end?: string | null;
  pm_time_start?: string | null;
  pm_time_end?: string | null;
  is_am_monday?: boolean;
  is_am_tuesday?: boolean;
  is_am_wednesday?: boolean;
  is_am_thursday?: boolean;
  is_am_friday?: boolean;
  is_am_saturday?: boolean;
  is_am_sunday?: boolean;
  is_pm_monday?: boolean;
  is_pm_tuesday?: boolean;
  is_pm_wednesday?: boolean;
  is_pm_thursday?: boolean;
  is_pm_friday?: boolean;
  is_pm_saturday?: boolean;
  is_pm_sunday?: boolean;
  remarks?: string | null;
  available_from?: string | null;
  available_to?: string | null;
  service_type?: string | null;
};

export const updateClinicLocationMassager = async (
  siteType: string,
  clinicLocationId: number,
  massagerId: number,
  payload: ClinicLocationMassagerUpdatePayload
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.put<{ success: boolean; message?: string }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-massagers/${massagerId}`,
    payload
  );
  return response.data;
};

export const deleteClinicLocationMassager = async (
  siteType: string,
  clinicLocationId: number,
  massagerId: number
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.delete<{ success: boolean; message?: string }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-massagers/${massagerId}`
  );
  return response.data;
};

/** Update: flat body (legacy) or BLOCK_A2 / BLOCK_R2. Omit a block key to leave that service unchanged. Null block = clear when 拠点 still offers the service but 資格などで無効. */
export type ClinicLocationManagerUpdatePayload =
  | ({ massager_id: number } & ClinicLocationManagerServiceBlock)
  | {
      massager_id: number;
      BLOCK_A2?: ClinicLocationManagerServiceBlock | null;
      BLOCK_R2?: ClinicLocationManagerServiceBlock | null;
    };

export const updateClinicLocationManager = async (
  siteType: string,
  clinicLocationId: number,
  managerId: number,
  payload: ClinicLocationManagerUpdatePayload
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.put<{ success: boolean; message?: string }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-managers/${managerId}`,
    payload
  );
  return response.data;
};

export const deleteClinicLocationManager = async (
  siteType: string,
  clinicLocationId: number,
  managerId: number
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.delete<{ success: boolean; message?: string }>(
    `/admin/${siteType}/clinic-locations/${clinicLocationId}/clinic-location-managers/${managerId}`
  );
  return response.data;
};
